import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import useGroups from './useGroups';
import useInvitations from './useInvitations';
import useOrganizations from './useOrganizations';
import usePlaylists from './usePlaylists';
import useUsers from './useUsers';
import useUserRoles from './useUserRoles';

const OrganizationMembersContext = React.createContext();

function OrganizationMembersProvider({
  useGraphQLMutation,
  useLazyGraphQLQuery,
  databaseUser,
  deleteOwnUser,
  handleHistory,
  hasActiveScimClient,
  hasSubscription,
  isBackOffice,
  isManager,
  isOrganizationAdmin,
  listView,
  locale,
  manageGroups,
  manageRoles,
  onClickSubscribe,
  onViewGroupActivity,
  organizationId,
  preSelectedUserIdToManageRole,
  restClient,
  totalLicences,
  user,
  ...props
}) {
  // Global states
  const [availableLicences, setAvailableLicences] = useState([]);
  const [formStatus, setFormStatus] = useState();
  const [modifyContentStatus, setModifyContentStatus] = useState({});
  const [manageGroupsOpen, setManageGroupsOpen] = useState(manageGroups);
  const [registeredMembers, setRegisteredMembers] = useState([]);
  const [selectedAction, setSelectedAction] = useState('');

  const {
    addGroupMembers,
    createGroupFormStatus,
    deleteGroup,
    deleteGroupMember,
    editGroupFormStatus,
    editGroupMembersOpen,
    getGroupsInfo,
    groupsInfo,
    loadingGroupsInfo,
    loadingUpdateGroupMembers,
    onCreateGroup,
    onDeleteGroup,
    onDeleteGroupConfirm,
    onDeleteGroupMember,
    onDeleteGroupMemberConfirm,
    onSaveEditGroup,
    onTransferGroup,
    requestToDeleteMemberGroups,
    requestToTransferUserGroups,
    setCreateGroupFormStatus,
    setDeleteGroup,
    setDeleteGroupMember,
    setEditGroupFormStatus,
    setEditGroupMembersOpen,
    setMemberToDeleteFromGroup,
    setTransferGroupStatus,
    setUpdateGroupMembersStatus,
    transferGroupStatus,
    updateGroupMembers,
    updateGroupMembersResponse,
    updateGroupMembersStatus,
  } = useGroups({
    useGraphQLMutation,
    useLazyGraphQLQuery,
    organizationId,
    registeredMembers,
    setFormStatus,
    setModifyContentStatus,
  });

  const {
    deleteInvitations,
    failureList,
    invitationsFormStatus,
    invitedMembers,
    inviteMembersOpen,
    loadingInvitedMembers,
    onCloseInviteMembers,
    resendInvitations,
    sendInvitations,
    setFailureList,
    setInvitationsFormStatus,
    setInviteMembersOpen,
    setShowMultipleFeedback,
    setSuccessList,
    showMultipleFeedback,
    successList,
  } = useInvitations({
    useGraphQLMutation,
    useLazyGraphQLQuery,
    isBackOffice,
    isManager,
    organizationId,
    setFormStatus,
  });

  const { getOrganizationManagers, loadingRegisteredMembers } =
    useOrganizations({
      useLazyGraphQLQuery,
      organizationId,
      registeredMembers,
      setRegisteredMembers,
    });

  const {
    loadingPlaylists,
    playlists,
    requestToDeleteMemberPlaylists,
    requestToTransferUserPlaylists,
  } = usePlaylists({
    useGraphQLMutation,
    useLazyGraphQLQuery,
    organizationId,
    registeredMembers,
    setModifyContentStatus,
  });

  const {
    administrateManagersRoleOptions,
    administrateUserRole,
    administrateUserRoleTo,
    administrateUserRoleOpen,
    changeRoleFormStatus,
    onSubmitAdministrateUserRoleHandler,
    setAdministrateUserRoleOpen,
    setChangeRoleFormStatus,
  } = useUserRoles({
    handleHistory,
    isOrganizationAdmin,
    manageRoles,
    modifyContentStatus,
    organizationId,
    registeredMembers,
    requestToDeleteMemberGroups,
    requestToDeleteMemberPlaylists,
    requestToTransferUserGroups,
    requestToTransferUserPlaylists,
    setModifyContentStatus,
    useGraphQLMutation,
  });

  const {
    deleteUser,
    isUserInOrganization,
    manageContentOwnership,
    onDeleteUser,
    onDeleteUserConfirm,
    onSubmitManageContentOwnership,
    setDeleteUser,
    setManageContentOwnership,
    setUserToDelete,
    transferContentChoiceOptions,
    transferContentOwnershipFor,
  } = useUsers({
    useGraphQLMutation,
    groupsInfo,
    invitedMembers,
    modifyContentStatus,
    organizationId,
    playlists,
    registeredMembers,
    requestToDeleteMemberGroups,
    requestToDeleteMemberPlaylists,
    requestToTransferUserGroups,
    requestToTransferUserPlaylists,
    setFormStatus,
    setModifyContentStatus,
  });

  const onCloseDelete = () => {
    // Setting all delete dialog states to allow having a single method
    setDeleteUser(false);
    setDeleteGroup(false);
    setDeleteGroupMember(false);
    setManageContentOwnership(false);
    setUserToDelete(null);
    setMemberToDeleteFromGroup(null);
    setFormStatus(null);
  };

  const downloadInvitations = () =>
    restClient.get(`/invitations/download/${organizationId}`);
  const downloadOrganizationGroupsHandler = () =>
    restClient.get(`/groups/download/${organizationId}`);
  const downloadOrganizationMembers = () =>
    restClient.get(`/members/download/${organizationId}`);
  const onViewGroupActivityHandler = (groupId) => {
    onViewGroupActivity(organizationId, groupId)();
  };

  useEffect(() => {
    const currentMembers = registeredMembers.length + invitedMembers.length;
    setAvailableLicences(Math.max(0, totalLicences - currentMembers));
  }, [invitedMembers, registeredMembers, totalLicences]);

  const contextValue = {
    // shared variables
    availableLicences,
    databaseUser,
    deleteOwnUser,
    downloadInvitations,
    downloadOrganizationGroups: downloadOrganizationGroupsHandler,
    downloadOrganizationMembers,
    formStatus,
    handleHistory,
    hasActiveScimClient,
    hasSubscription,
    isBackOffice,
    isManager,
    listView,
    locale,
    manageGroupsOpen,
    modifyContentStatus,
    onCloseDelete,
    onClickSubscribe,
    onViewGroupActivity: onViewGroupActivityHandler,
    preSelectedUserIdToManageRole,
    registeredMembers,
    restClient,
    selectedAction,
    setManageGroupsOpen,
    setModifyContentStatus,
    setSelectedAction,
    totalLicences,
    user,
    // context uses
    groups: {
      addGroupMembers,
      createGroupFormStatus,
      deleteGroup,
      deleteGroupMember,
      editGroupFormStatus,
      editGroupMembersOpen,
      getGroupsInfo,
      groupsInfo,
      loadingGroupsInfo,
      loadingUpdateGroupMembers,
      onCreateGroup,
      onDeleteGroup,
      onDeleteGroupConfirm,
      onDeleteGroupMember,
      onDeleteGroupMemberConfirm,
      onSaveEditGroup,
      onTransferGroup,
      setCreateGroupFormStatus,
      setEditGroupFormStatus,
      setEditGroupMembersOpen,
      setTransferGroupStatus,
      setUpdateGroupMembersStatus,
      transferGroupStatus,
      updateGroupMembers,
      updateGroupMembersResponse,
      updateGroupMembersStatus,
    },
    invitations: {
      deleteInvitations,
      failureList,
      invitationsFormStatus,
      invitedMembers,
      inviteMembersOpen,
      loadingInvitedMembers,
      onCloseInviteMembers,
      resendInvitations,
      sendInvitations,
      setFailureList,
      setInvitationsFormStatus,
      setInviteMembersOpen,
      setShowMultipleFeedback,
      setSuccessList,
      showMultipleFeedback,
      successList,
    },
    organizations: { getOrganizationManagers, loadingRegisteredMembers },
    playlists: {
      loadingPlaylists,
      playlists,
    },
    userRoles: {
      administrateManagersRoleOptions,
      administrateUserRole,
      administrateUserRoleOpen,
      administrateUserRoleTo,
      changeRoleFormStatus,
      onSubmitAdministrateUserRoleHandler,
      setAdministrateUserRoleOpen,
      setChangeRoleFormStatus,
    },
    users: {
      deleteUser,
      isUserInOrganization,
      manageContentOwnership,
      onDeleteUser,
      onDeleteUserConfirm,
      onSubmitManageContentOwnership,
      transferContentChoiceOptions,
      transferContentOwnershipFor,
    },
  };

  return (
    <OrganizationMembersContext.Provider value={contextValue} {...props} />
  );
}

OrganizationMembersProvider.propTypes = {
  useGraphQLMutation: PropTypes.func.isRequired,
  useLazyGraphQLQuery: PropTypes.func.isRequired,
  databaseUser: PropTypes.shape({
    email: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  deleteOwnUser: PropTypes.bool,
  handleHistory: PropTypes.func,
  hasActiveScimClient: PropTypes.bool,
  hasSubscription: PropTypes.bool.isRequired,
  isBackOffice: PropTypes.bool,
  isManager: PropTypes.bool,
  isOrganizationAdmin: PropTypes.bool,
  listView: PropTypes.string,
  locale: PropTypes.string,
  manageGroups: PropTypes.bool,
  onClickSubscribe: PropTypes.func,
  onViewGroupActivity: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
  restClient: PropTypes.func.isRequired,
  totalLicences: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  user: PropTypes.object,
  manageRoles: PropTypes.bool,
  preSelectedUserIdToManageRole: PropTypes.string,
};

OrganizationMembersProvider.defaultProps = {
  handleHistory: null,
  hasActiveScimClient: false,
  isManager: false,
  isOrganizationAdmin: false,
  listView: 'members',
  manageGroups: false,
  manageRoles: false,
};

function useOrganizationMembers() {
  const context = React.useContext(OrganizationMembersContext);
  if (!context) {
    throw new Error(
      'useOrganizationMembers must be used within a OrganizationMembersProvider'
    );
  }

  return context;
}

export { OrganizationMembersProvider, useOrganizationMembers };
